import { filterErrorsByField } from 'Automations/automationEditor/utils'
import { Box, Grid } from '@sequencehq/core-components'
import { Fragment, useEffect, useMemo } from 'react'

import {
  ConditionPropertyEditorComponent,
  PERCENTAGE_UNIT
} from 'Automations/nodeDefinitions/conditions'
import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { useDefaultCurrency } from 'SettingsV2/hooks/useCurrencies'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import { PriceUnitSelector } from '../../../../switch/components/GroupEditor/inputs/components/PriceUnitSelector'
import { PriceValueField } from '../../../../switch/components/GroupEditor/inputs/components/PriceValueField'
import { ProductSelector } from '../../../../switch/components/GroupEditor/inputs/components/ProductSelector'
import {
  appendTestId,
  DiscountSubOperator,
  isDiscountSubOperator,
  ProductConditionValue,
  readValue,
  usePrimaryOperatorState
} from './ProductPropertyEditor.helpers'

const SUB_OPERATOR_OPTIONS: Array<{
  label: string
  value: DiscountSubOperator
}> = [
  { label: 'Equals', value: 'EQUALS' },
  { label: 'Less than', value: 'LESS_THAN' },
  { label: 'More than', value: 'MORE_THAN' }
]

/**
 * Editor for the PRODUCT.DISCOUNT operator. Owns the product picker,
 * primary operator dropdown (DISCOUNT / HAS_DISCOUNT), and the second
 * row holding the sub-operator + unit + amount.
 */
export const DiscountOperatorEditor: ConditionPropertyEditorComponent = ({
  'data-testid': dataTestId,
  operator,
  value,
  onChange,
  operators,
  siblingConditions,
  validationErrors,
  focusOnInitialLoad
}) => {
  const { productId, operator: subOperator, amount, unit } = readValue(value)
  const defaultCurrency = useDefaultCurrency()
  const effectiveUnit = unit ?? defaultCurrency ?? PERCENTAGE_UNIT

  const {
    operatorOptions,
    primaryOperatorDisabled,
    onPrimaryOperatorChange,
    sameProductSiblings
  } = usePrimaryOperatorState({
    operator,
    productId,
    operators,
    siblingConditions,
    onChange
  })

  const subOperatorDisabled = !productId

  // Sub-operator filter: hide same-twice, hide LESS_THAN/MORE_THAN when
  // EQUALS used by a DISCOUNT sibling, hide EQUALS when range is used.
  const subOperatorOptions = useMemo(() => {
    const usedSubOperators = new Set(
      sameProductSiblings
        .filter(s => s.operator === 'DISCOUNT')
        .map(s => s.subOperator)
        .filter(isDiscountSubOperator)
    )

    const rangeUsed =
      usedSubOperators.has('LESS_THAN') || usedSubOperators.has('MORE_THAN')
    const equalsUsed = usedSubOperators.has('EQUALS')
    return SUB_OPERATOR_OPTIONS.filter(o => {
      if (o.value === subOperator) return true
      if (usedSubOperators.has(o.value)) return false
      if (['LESS_THAN', 'MORE_THAN'].includes(o.value) && equalsUsed) {
        return false
      }
      if (o.value === 'EQUALS' && rangeUsed) return false
      return true
    })
  }, [sameProductSiblings, subOperator])

  const storedSubOperator = isDiscountSubOperator(subOperator)
    ? subOperator
    : undefined
  const selectedSubOperator =
    subOperatorOptions.find(option => option.value === storedSubOperator)
      ?.value ?? (productId ? subOperatorOptions.at(0)?.value : undefined)
  const showAmountUnit = Boolean(selectedSubOperator)

  const update = (next: Partial<ProductConditionValue>) =>
    onChange({
      value: {
        productId: next.productId ?? productId ?? '',
        operator: next.operator ?? selectedSubOperator ?? '',
        amount: next.amount ?? amount ?? '',
        unit: next.unit ?? unit ?? effectiveUnit
      }
    })

  // Store derived defaults so saved rows match what their controls display.
  useEffect(() => {
    const shouldStoreSubOperator = selectedSubOperator !== storedSubOperator
    const shouldStoreUnit = effectiveUnit !== unit
    if (!shouldStoreSubOperator && !shouldStoreUnit) return

    onChange({
      value: {
        productId: productId ?? '',
        operator: selectedSubOperator ?? '',
        amount: amount ?? '',
        unit: effectiveUnit
      }
    })
  }, [
    effectiveUnit,
    unit,
    selectedSubOperator,
    storedSubOperator,
    productId,
    amount,
    onChange
  ])

  return (
    <Fragment>
      <Grid
        gridTemplateColumns="1fr 1fr"
        gap="8px"
        gridTemplateAreas='"product operator" "subRow subRow"'
      >
        <Box gridArea="product">
          <ProductSelector
            data-testid={appendTestId(dataTestId, 'product')}
            value={productId ?? ''}
            onChange={selected => update({ productId: String(selected) })}
            validationErrors={filterErrorsByField(
              validationErrors?.value,
              'productId'
            )}
            initiallyOpen={focusOnInitialLoad}
          />
        </Box>
        <Box gridArea="operator">
          <InputSelectControlField
            data-testid={appendTestId(dataTestId, 'operator')}
            placeholder="Select..."
            options={operatorOptions}
            value={operator ?? ''}
            disabled={primaryOperatorDisabled}
            onChange={onPrimaryOperatorChange}
            validationErrors={validationErrors?.operator}
          />
        </Box>
        {!!productId && (
          <Grid
            data-testid={appendTestId(dataTestId, 'subRow')}
            gridArea="subRow"
            gridTemplateColumns={
              showAmountUnit
                ? `${SUB_OPERATOR_COLUMN_WIDTH} 72px minmax(0, 1fr)`
                : SUB_OPERATOR_COLUMN_WIDTH
            }
            columnGap="8px"
          >
            <InputSelectControlField
              data-testid={appendTestId(dataTestId, 'subOperator')}
              value={selectedSubOperator ?? ''}
              onChange={selected =>
                update({ operator: selected as DiscountSubOperator })
              }
              options={subOperatorOptions}
              placeholder="Select..."
              disabled={subOperatorDisabled}
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'operator'
              )}
            />
            {showAmountUnit && (
              <>
                <PriceUnitSelector
                  data-testid={appendTestId(dataTestId, 'unit')}
                  value={effectiveUnit}
                  onChange={nextUnit => update({ unit: nextUnit })}
                  allowCurrency
                  allowPercentage
                />
                <PriceValueField
                  data-testid={appendTestId(dataTestId, 'value')}
                  value={amount ?? ''}
                  unit={effectiveUnit}
                  onChange={nextAmount => update({ amount: nextAmount })}
                  validationErrors={filterErrorsByField(
                    validationErrors?.value,
                    'amount'
                  )}
                />
              </>
            )}
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}
